/**
 * Hold Reason field for the order modal — set, change or release a Sage hold.
 *
 * Drafts: the select stores the code on the portal order (it rides the Sage
 * submission) via the caller's PATCH path.
 *
 * Submitted orders still open in Sage: the select and the "Release hold"
 * button write to live Sage through PUT /portal/orders/{id}/hold. A release
 * puts an explicit empty CancelReasonCode on the wire (sage-server #10); the
 * portal keeps showing the hold until its reverse-sync sees Sage report the
 * order Open, so the status line under the field walks through
 * "Release sent" -> "Released · Sage shows Open" by polling the hold status.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type {
  CancelReasonCodeOption,
  OrderHoldStatus,
} from '../../services/portalOrders';
import type { OrderRecord } from '../../types/orders';
import styles from './OrderDetailModal.module.css';

/** How often the field re-reads the hold status while a write is in flight. */
export const HOLD_STATUS_POLL_MS = 30_000;

export interface HoldReasonFieldProps {
  order: OrderRecord;
  /** orders:manage on the viewer. */
  canEdit: boolean;
  /** Draft / submission_failed — the code is stored locally only. */
  isDraft: boolean;
  /** Order is present and open in the Sage SO mirror (not shipped/invoiced). */
  editableInSage: boolean;
  cancelReasonCodes: CancelReasonCodeOption[];
  /** Draft path: the caller's single-field PATCH. */
  onDraftChange: (code: string | null) => void;
  /** Fired with the refreshed order after a submitted-order hold write. */
  onOrderUpdated?: (order: OrderRecord) => void;
}

function fmtDateTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

type Tone = 'ok' | 'pending' | 'warn' | 'error';

const TONE_COLOR: Record<Tone, string> = {
  ok: 'var(--color-success, #1a7f37)',
  pending: 'var(--color-warning-text, #9a6700)',
  warn: 'var(--color-warning-text, #9a6700)',
  error: 'var(--color-danger, #b42318)',
};

/** The one-line summary of where the hold stands, or null when there is nothing to say. */
export function describeHoldStatus(status: OrderHoldStatus): { text: string; tone: Tone } | null {
  const code = status.portal_code;
  switch (status.state) {
    case 'confirmed': {
      const parts = [`Hold confirmed in Sage${code ? ` (${code})` : ''}`];
      if (status.set_by) parts.push(`set by ${status.set_by}`);
      const when = fmtDateTime(status.set_at);
      if (when) parts.push(when);
      return { text: parts.join(' · '), tone: 'ok' };
    }
    case 'set_pending':
      if (status.overdue) {
        return {
          text: `Hold sent to Sage${code ? ` (${code})` : ''} · not reflected in Sage after 15+ min · check the Sage write log`,
          tone: 'error',
        };
      }
      return {
        text: `Hold sent to Sage${code ? ` (${code})` : ''} · applies within ~2 min · the portal re-checks Sage and confirms here`,
        tone: 'pending',
      };
    case 'held_unlabeled':
      return {
        text: `Held in Sage, but Sage dropped the reason label${code ? ` (${code})` : ''} · pick an active reason to relabel it`,
        tone: 'warn',
      };
    case 'release_pending':
      if (status.overdue) {
        return {
          text: 'Release sent to Sage · not reflected in Sage after 15+ min · check the Sage write log',
          tone: 'error',
        };
      }
      return {
        text: 'Release sent to Sage · applies within ~2 min · the portal re-checks Sage and confirms here',
        tone: 'pending',
      };
    case 'released': {
      const when = fmtTime(status.confirmed_at);
      return {
        text: `Released · Sage shows Open (no hold)${when ? ` · confirmed ${when}` : ''} · logged in the order timeline`,
        tone: 'ok',
      };
    }
    case 'no_hold':
    default:
      return null;
  }
}

const HoldReasonField: React.FC<HoldReasonFieldProps> = ({
  order,
  canEdit,
  isDraft,
  editableInSage,
  cancelReasonCodes,
  onDraftChange,
  onOrderUpdated,
}) => {
  const inSage = !!order.sage_order_no;
  const sageEditable = canEdit && !isDraft && inSage && editableInSage;
  const [status, setStatus] = useState<OrderHoldStatus | null>(null);
  const [busy, setBusy] = useState<'set' | 'release' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Bumped each time a hold write's response lands. A status read that STARTED
  // before the latest write landed is stale: applying it would replace the
  // write's fresher set_pending / release_pending status with the old state
  // and, because that old state is not pending, leave the poll unarmed, so the
  // line would sit stale until the modal is reopened.
  const writeSeqRef = useRef(0);

  const refreshStatus = useCallback(async () => {
    const seqAtStart = writeSeqRef.current;
    try {
      const s = await portalOrderService.getHoldStatus(order.id);
      if (seqAtStart !== writeSeqRef.current) return; // a write landed meanwhile
      setStatus(s);
    } catch {
      // Status is advisory; a failed read leaves the last known state.
    }
  }, [order.id]);

  // Fetch on mount / order change for submitted orders.
  useEffect(() => {
    if (!inSage || isDraft) {
      setStatus(null);
      return;
    }
    void refreshStatus();
  }, [inSage, isDraft, refreshStatus]);

  // Poll while a write is in flight so the line moves to "confirmed" on its own.
  const pending = status?.state === 'set_pending' || status?.state === 'release_pending';
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!pending) return undefined;
    pollRef.current = setInterval(() => {
      void refreshStatus();
    }, HOLD_STATUS_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [pending, refreshStatus]);

  const submitHold = async (code: string | null) => {
    if (busy) return;
    setBusy(code === null ? 'release' : 'set');
    setError(null);
    try {
      const resp = await portalOrderService.setHold(order.id, code);
      writeSeqRef.current += 1;
      setStatus(resp.hold);
      if (resp.succeeded) {
        onOrderUpdated?.(resp.order);
      } else {
        setError(resp.message);
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not update the hold. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const currentCode = order.cancel_reason_code || '';
  const activeCodes = cancelReasonCodes.filter((rc) => !rc.is_inactive);
  const currentInactive = currentCode
    ? cancelReasonCodes.find((rc) => rc.cancel_reason_code === currentCode && rc.is_inactive)
    : undefined;

  const renderOptions = (emptyLabel: string) => (
    <>
      <option value="">{emptyLabel}</option>
      {activeCodes.map((rc) => (
        <option key={rc.cancel_reason_code} value={rc.cancel_reason_code}>
          {rc.description || rc.cancel_reason_code}
        </option>
      ))}
      {/* The order already carries a code Sage has since deactivated: keep it
          selectable-as-current (so the select shows the truth) but disabled,
          and say why the CSR must pick another. Sage refuses inactive codes —
          the hold lands, the label doesn't. */}
      {currentInactive && (
        <option value={currentCode} disabled>
          {`${currentInactive.description || currentCode} (inactive in Sage — pick another)`}
        </option>
      )}
    </>
  );

  // ── Draft: local select (rides the submission payload) ──
  if (canEdit && isDraft) {
    return (
      <div className={styles.infoRow}>
        <span className={styles.infoLabel}>Hold Reason</span>
        <span className={styles.infoValue}>
          <select
            value={currentCode}
            onChange={(e) => onDraftChange(e.target.value || null)}
            className={styles.inlineEditInput}
            style={{ width: '180px' }}
            data-testid="order-detail-hold-reason"
          >
            {renderOptions('None (no hold)')}
          </select>
        </span>
      </div>
    );
  }

  const summary = status ? describeHoldStatus(status) : null;
  const releasePending = status?.state === 'release_pending';
  const showRelease = sageEditable && !!currentCode;

  // ── Submitted + open in Sage: live hold set / release ──
  if (sageEditable) {
    return (
      <div className={styles.infoRow} style={{ alignItems: 'flex-start' }}>
        <span className={styles.infoLabel} style={{ paddingTop: '4px' }}>Hold Reason</span>
        <span className={styles.infoValue} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', maxWidth: '380px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={currentCode}
              disabled={busy !== null}
              onChange={(e) => {
                const next = e.target.value || null;
                if (next === (order.cancel_reason_code || null)) return;
                void submitHold(next);
              }}
              className={styles.inlineEditInput}
              style={{ width: '200px' }}
              data-testid="order-detail-hold-reason"
              title={
                currentCode
                  ? 'Pick another reason to change the hold in Sage, or None to release it'
                  : 'Pick a reason to put this order on hold in Sage'
              }
            >
              {renderOptions('None (release hold)')}
            </select>
            {showRelease && (
              <button
                type="button"
                data-testid="order-detail-hold-release"
                disabled={busy !== null || releasePending}
                onClick={() => void submitHold(null)}
                title="Take this order off hold in Sage (CancelReasonCode cleared, order returns to Open)"
                style={{
                  padding: '3px 10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '4px',
                  border: '1px solid var(--color-danger, #b42318)',
                  color: 'var(--color-danger, #b42318)',
                  background: 'transparent',
                  cursor: busy !== null || releasePending ? 'wait' : 'pointer',
                  opacity: busy !== null || releasePending ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {busy === 'release' || releasePending ? 'Releasing…' : 'Release hold'}
              </button>
            )}
          </span>
          {summary && (
            <span
              data-testid="order-detail-hold-status"
              style={{ fontSize: '12px', fontWeight: 400, lineHeight: 1.4, textAlign: 'right', color: TONE_COLOR[summary.tone] }}
            >
              {summary.text}
            </span>
          )}
          {error && (
            <span
              data-testid="order-detail-hold-error"
              style={{ fontSize: '12px', fontWeight: 400, textAlign: 'right', color: TONE_COLOR.error }}
            >
              {error}
            </span>
          )}
        </span>
      </div>
    );
  }

  // ── Read-only (viewer, shipped/invoiced, or not yet numbered by Sage) ──
  const match = currentCode ? cancelReasonCodes.find((rc) => rc.cancel_reason_code === currentCode) : undefined;
  return (
    <div className={styles.infoRow} style={summary ? { alignItems: 'flex-start' } : undefined}>
      <span className={styles.infoLabel}>Hold Reason</span>
      <span className={styles.infoValue} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
        <span data-testid="order-detail-hold-reason-readonly">
          {currentCode ? match?.description || currentCode : '—'}
        </span>
        {summary && (
          <span
            data-testid="order-detail-hold-status"
            style={{ fontSize: '12px', fontWeight: 400, lineHeight: 1.4, textAlign: 'right', color: TONE_COLOR[summary.tone] }}
          >
            {summary.text}
          </span>
        )}
      </span>
    </div>
  );
};

export default HoldReasonField;
